import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import AssetExplorer from "../../components/common/AssetExplorer";
import DashboardBreadcrumbs from "../../components/DashboardBreadcrumbs";
import { Button as OpusButton } from "../../components/common/opus";
import { useAlert } from "../../contexts/AlertContext";
import apiService from "../../services/api.service";
import {
  logicalToRelative,
  urlSegmentToLogical,
} from "../../components/common/AssetExplorer/pathUtils";

/**
 * Scene-level asset manager.
 *
 * Route: /dashboard/scene-management/:orgId/:sceneId/assets/*
 *
 * The route's splat (the `*` portion of the URL) holds the in-folder path
 * relative to `assets/`. So `/.../assets`           → logical "assets"
 *                          `/.../assets/branding`  → logical "assets/branding"
 *                          `/.../assets/a/b/c`     → logical "assets/a/b/c"
 */
const SceneAssetsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { sceneId, orgId, "*": splat = "" } = useParams();
  const alert = useAlert();

  const [loading, setLoading] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [sceneData, setSceneData] = useState(null);

  const orgName = useMemo(() => {
    if (!sceneData || !organizations.length) return "";
    const org = organizations.find((o) => o._id === sceneData.organization_id);
    return org?.name?.toLowerCase().replace(/\s+/g, "-") || "";
  }, [organizations, sceneData]);

  const scenePathName = useMemo(() => {
    if (!sceneData) return "";
    return sceneData.scene_name?.toLowerCase().replace(/\s+/g, "-") || "";
  }, [sceneData]);

  useEffect(() => {
    apiService.getOrganizationsFromScene()
      .then(setOrganizations)
      .catch(() => alert.error("Failed to load organizations"));
  }, []); // eslint-disable-line

  useEffect(() => {
    if (!sceneId || !orgId) return;
    setLoading(true);
    apiService.getScenesByOrganization(orgId)
      .then(({ scenes }) => {
        const scene = scenes.find((s) => (s._id || s.id) === sceneId);
        if (scene) {
          setSceneData({
            _id: scene._id || scene.id,
            scene_name: scene.name || scene.scene_name,
            organization_id: orgId,
          });
        } else {
          alert.error("Scene not found");
        }
      })
      .catch(() => alert.error("Failed to load scene details"))
      .finally(() => setLoading(false));
  }, [sceneId, orgId]); // eslint-disable-line

  // -- url <-> path bridge ---------------------------------------------------
  const currentPath = urlSegmentToLogical(splat);

  const handlePathChange = useCallback((nextLogical) => {
    const rel = logicalToRelative(nextLogical);
    const base = `/dashboard/scene-management/${orgId}/${sceneId}/assets`;
    const next = rel ? `${base}/${rel}` : base;
    if (next !== location.pathname) navigate(next);
  }, [orgId, sceneId, navigate, location.pathname]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!sceneData) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh" }}>
        <Typography>Scene not found</Typography>
      </Box>
    );
  }

  // breadcrumb trail mirrors the in-folder path, with each segment clickable.
  const folderSegments = logicalToRelative(currentPath).split('/').filter(Boolean);
  const baseLink = `/dashboard/scene-management/${orgId}/${sceneId}/assets`;
  const folderCrumbs = folderSegments.map((segment, idx) => ({
    label: segment,
    to: `${baseLink}/${folderSegments.slice(0, idx + 1).join('/')}`,
  }));

  return (
    <Box sx={{ height: "100vh", display: "flex", flexDirection: "column" }}>
      <DashboardBreadcrumbs
        trail={[
          { label: "Scenes", to: `/dashboard/scene-management` },
          { label: sceneData.scene_name, to: `/dashboard/scene-management/${orgId}/${sceneId}/edit` },
          { label: "Assets", to: baseLink },
          ...folderCrumbs,
        ]}
        rightAction={
          <OpusButton variant="secondary" size="sm" icon="chevronLeft" onClick={() => navigate(`/dashboard/scene-management`)}>Back</OpusButton>
        }
      />

      <Box sx={{ flex: 1, overflow: "hidden", p: 2 }}>
        {/* OPUS-913: surface-1 panel so the file explorer matches Scene Management's BG. */}
        <Box sx={{ height: "100%", overflow: "hidden", background: "var(--surface-1)", border: "1px solid var(--border)", borderRadius: "var(--r-lg)", p: 1.5 }}>
          <AssetExplorer
            organizationName={orgName}
            sceneName={scenePathName}
            path={currentPath}
            onPathChange={handlePathChange}
            onFileSelect={() => {}}
            hideBreadcrumbs
          />
        </Box>
      </Box>
    </Box>
  );
};

export default SceneAssetsPage;
